import { toast } from '@montugroup/design-system';
import type { DefaultError, Mutation, Query } from '@tanstack/react-query';
import { onlineManager } from '@tanstack/react-query';
import type { NetworkError } from './types';
import { StatusCode } from './types';
import { errorLogger, generateErrorMessage } from './utils';

export const ERROR_PAGE_STATUSES = [
  StatusCode.Unauthorised,
  StatusCode.BadRequest,
  StatusCode.Forbidden,
  StatusCode.NotFound,
];

export const useErrorHandler = () => {
  const handleErrorAlert = (
    error: DefaultError,
    config: Query<unknown, unknown, unknown> | Mutation<unknown, unknown, unknown>,
  ) => {
    const networkError = error as NetworkError;
    // check if the error is handled at component level
    const isErrorHandled = config?.meta?.isErrorHandled;
    // enable error logging
    errorLogger(networkError);

    if (isErrorHandled) {
      return;
    }

    toast.error({
      message: generateErrorMessage(networkError.status, !onlineManager.isOnline()),
    });
  };

  const handleErrorPage = (error: DefaultError) => {
    const networkError = error as NetworkError;

    if (ERROR_PAGE_STATUSES.includes(networkError.status)) {
      return true;
    }

    return false;
  };

  const originalConsoleError = console.error;
  /**
   * Overrides console.error to redact specific messages with corresponding replacements.
   * @param redactionMap An object where keys are substrings to match, values are replacement texts.
   */
  function handleConsoleErrorOverride(redactionMap: Record<string, string>) {
    console.error = (...args: unknown[]) => {
      if (args.length > 0 && typeof args[0] === 'string') {
        const originalMessage = args[0];
        for (const [matchStr, replacement] of Object.entries(redactionMap)) {
          if (originalMessage.includes(matchStr)) {
            originalConsoleError(replacement);
            return;
          }
        }
      }
      originalConsoleError.apply(console, args);
    };
  }

  return { handleErrorAlert, handleErrorPage, handleConsoleErrorOverride };
};
